import React, { useState } from 'react'
import { gql, useQuery } from '@apollo/client'
import { useSnackbar } from 'notistack'
import { useChatContext } from 'stream-chat-react'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Badge from '@material-ui/core/Badge'
import IconButton from '@material-ui/core/IconButton'
import TuneOutlinedIcon from '@material-ui/icons/TuneOutlined'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction'
import Checkbox from '@material-ui/core/Checkbox'
import CircularProgress from '@material-ui/core/CircularProgress'
import Button from '@material-ui/core/Button'
import { useAuth } from 'lib/auth'
import { useFindOrCreateChannelWithUserIds } from 'lib/stream-messaging/hooks'
import ContactFilterOptionsDialog from './contact-filter-options-dialog'

const GET_MESSAGING_CONTACTS = gql`
  query MessagingContacts {
    messagingContacts {
      type
      id
      name
      image
      externalId
      description
      sources
    }
    messagingContactSource: __type(name: "MessagingContactSource") {
      enumValues {
        name
        description
      }
    }
  }
`

const NewConversationForm = ({ onSuccess, onCancel }) => {
  const findOrCreateChannelWithUserIds = useFindOrCreateChannelWithUserIds()
  const { setActiveChannel } = useChatContext()
  const { enqueueSnackbar } = useSnackbar()
  const { data, loading, error, refetch } = useQuery(GET_MESSAGING_CONTACTS)
  const [selectedSourceFilters, setSelectedSourceFilters] = useState([])
  const [selectedContactExternalIds, setSelectedContactExternalIds] = useState(
    [],
  )
  const { isConsumer, isCoach } = useAuth()

  const toggleContactSelection = contact => {
    const currentIndex = selectedContactExternalIds.indexOf(contact.externalId)
    const newSelection = [...selectedContactExternalIds]

    if (currentIndex === -1) {
      newSelection.push(contact.externalId)
    } else {
      newSelection.splice(currentIndex, 1)
    }

    setSelectedContactExternalIds(newSelection)
  }

  const isContactSelected = contact =>
    selectedContactExternalIds.includes(contact.externalId)

  const createConversation = async () => {
    const channel = await findOrCreateChannelWithUserIds(
      selectedContactExternalIds,
    )
    if (channel) {
      setActiveChannel(channel)
      onSuccess && onSuccess()
    } else {
      enqueueSnackbar(
        'Unable to create or join conversation. Please try again later.',
        {
          variant: 'error',
        },
      )
    }
  }

  if (loading) return <CircularProgress />
  if (error) {
    return (
      <>
        <Box mb={2}>
          <Typography variant="body1">Unable to load contacts</Typography>
        </Box>
        <Button variant="contained" color="primary" onClick={() => refetch()}>
          Reload
        </Button>
      </>
    )
  }

  const { messagingContacts, messagingContactSource } = data

  const sourceFilterOptions = Array.from(
    new Set(messagingContacts.flatMap(contact => contact.sources)),
  ).map(opt => messagingContactSource.enumValues.find(e => e.name === opt))
  const selectedSourceFilterValues = selectedSourceFilters.map(f => f.name)
  const filteredContacts =
    selectedSourceFilterValues.length > 0
      ? messagingContacts.filter(mc =>
        mc.sources.some(s => selectedSourceFilterValues.includes(s)),
      )
      : messagingContacts

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2">Contacts</Typography>
        <ContactFilterOptionsDialog
          sourceFilterOptions={sourceFilterOptions}
          selectedSourceFilters={selectedSourceFilters}
          setSelectedSourceFilters={setSelectedSourceFilters}
          trigger={
            <IconButton color="primary">
              <Badge
                color="primary"
                variant="dot"
                invisible={selectedSourceFilters.length === 0}
              >
                <TuneOutlinedIcon />
              </Badge>
            </IconButton>
          }
        />
      </Box>
      {filteredContacts.length > 0 ? (
        <Box style={{ maxHeight: '400px', overflowY: 'auto' }}>
          <List>
            {filteredContacts.map(contact => {
              const key = `${contact.type}-${contact.id}`
              return (
                <ListItem
                  key={key}
                  button
                  onClick={() => toggleContactSelection(contact)}
                >
                  <ListItemText
                    id={key}
                    primary={contact.name}
                    secondary={contact.description}
                  />
                  <ListItemSecondaryAction>
                    <Checkbox
                      edge="end"
                      onChange={() => toggleContactSelection(contact)}
                      checked={isContactSelected(contact)}
                      inputProps={{ 'aria-labelledby': key }}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              )
            })}
          </List>
        </Box>
      ) : (
        <Box mt={2}>
          <Typography variant="body1">No contacts found</Typography>
          {isCoach && selectedSourceFilterValues.length === 0 && (
            <Typography variant="body2">
              Your students and leads will appear here once they have been added
            </Typography>
          )}
          {isConsumer && selectedSourceFilterValues.length === 0 && (
            <Typography variant="body2">
              It doesn't look like you have any contacts yet
            </Typography>
          )}
          {selectedSourceFilterValues.length !== 0 && (
            <Typography variant="body2">
              Try removing some of your filters and search again
            </Typography>
          )}
        </Box>
      )}
      <Box display="flex" mt={4}>
        <Box mr={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={createConversation}
            disabled={selectedContactExternalIds.length === 0}
          >
            Create
          </Button>
        </Box>
        <Button autoFocus={filteredContacts.length === 0} onClick={onCancel}>
          Cancel
        </Button>
      </Box>
    </>
  )
}

export default NewConversationForm
